import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Edit2, Package, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { productAPI } from '@/lib/api';

const MAX_PRODUCTS_PER_SELLER = 3;

const SellerProducts = () => {
  const navigate = useNavigate();
  const [user] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    price_usd: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        productAPI.getProducts({ seller_id: user.id }),
        productAPI.getCategories()
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price_usd: parseFloat(formData.price_usd)
      };

      if (editingProduct) {
        await productAPI.updateProduct(editingProduct.id, data);
        toast.success('Product updated successfully');
      } else {
        await productAPI.createProduct(data);
        toast.success('Product created successfully');
      }

      setShowAddModal(false);
      setEditingProduct(null);
      setFormData({ name: '', description: '', category: '', price_usd: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDelete = async (productId) => {
    if (!confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await productAPI.deleteProduct(productId);
      toast.success('Product deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      category: product.category,
      price_usd: product.price_usd.toString()
    });
    setShowAddModal(true);
  };

  const canAddMore = products.length < MAX_PRODUCTS_PER_SELLER;

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-surface py-8">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate('/seller/dashboard')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-chivo font-bold text-slate-900">My Products</h1>
            <p className="text-slate-600">
              {products.length}/{MAX_PRODUCTS_PER_SELLER} products listed
            </p>
          </div>
          
          <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-product-btn"
                disabled={!canAddMore}
                onClick={() => {
                  if (!canAddMore) {
                    toast.error(`Maximum ${MAX_PRODUCTS_PER_SELLER} products allowed`);
                    return;
                  }
                  setEditingProduct(null);
                  setFormData({ name: '', description: '', category: '', price_usd: '' });
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            </DialogTrigger>
            
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {editingProduct ? 'Edit Product' : 'Add New Product'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Product Name *</Label>
                  <Input
                    data-testid="product-name-input"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Fresh Taro Root"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    data-testid="product-description-input"
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe your product..."
                    required
                    rows={3}
                  />
                </div>
                
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger data-testid="product-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="price">Price (USD) *</Label>
                  <Input
                    data-testid="product-price-input"
                    id="price"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.price_usd}
                    onChange={(e) => setFormData({ ...formData, price_usd: e.target.value })}
                    placeholder="0.00"
                    required
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setShowAddModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    data-testid="save-product-btn"
                    type="submit"
                    className="flex-1"
                  >
                    {editingProduct ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Product Limit Warning */}
        {!canAddMore && (
          <Card className="mb-6 p-4 bg-amber-50 border-amber-200">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">Product limit reached</p>
                <p className="text-sm text-amber-700">
                  You've reached the maximum of {MAX_PRODUCTS_PER_SELLER} products. Delete an existing product to add a new one.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Products List */}
        {products.length === 0 ? (
          <Card className="p-12 text-center">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h4 className="text-xl font-chivo font-semibold text-slate-900 mb-2">
              No products yet
            </h4>
            <p className="text-slate-600 mb-6">
              Add your first product to start selling
            </p>
            <Button onClick={() => setShowAddModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Product
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {products.map((product) => (
              <Card
                key={product.id}
                data-testid="product-card"
                className="p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-chivo font-semibold text-slate-900 mb-1">
                      {product.name}
                    </h3>
                    <p className="text-sm text-slate-600 mb-2 line-clamp-2">
                      {product.description}
                    </p>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="px-2 py-1 bg-slate-100 rounded">
                        {categories.find(c => c.id === product.category)?.name || product.category}
                      </span>
                    </div>
                  </div>
                  
                  <div className="text-right ml-4">
                    <p className="text-2xl font-chivo font-bold text-slate-900">
                      ${product.price_usd}
                    </p>
                    <div className="flex gap-2 mt-3">
                      <Button
                        data-testid={`edit-product-${product.id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(product)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`delete-product-${product.id}`}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(product.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SellerProducts;
